import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Radio, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { useSite } from "@/context/SiteContext";
import { orderedMenu } from "@/lib/menu";

export const LangToggle = () => {
  const { lang, setLang, switchOn } = useLang();
  if (!switchOn) return null;
  return (
    <div data-testid="language-toggle" className="relative flex rounded-full border border-[#D4AF37]/30 p-1 font-ui text-xs font-semibold">
      {["en", "mh"].map((l) => (
        <button
          key={l}
          data-testid={`language-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`relative z-10 rounded-full px-3 py-1.5 uppercase tracking-wider transition-colors duration-300 ${lang === l ? "bg-[#D4AF37] text-[#0B111E]" : "text-[#F4EEE2]/80 hover:text-[#F4EEE2]"}`}
        >
          {l === "en" ? "EN" : "MH"}
        </button>
      ))}
    </div>
  );
};

const linkCls = (vertical) => ({ isActive }) =>
  `nav-link font-ui ${vertical ? "font-display text-4xl" : "text-sm"} tracking-wide transition-colors duration-300 ${isActive ? "text-[#D4AF37] is-active" : "text-[#F4EEE2]/80 hover:text-[#F4EEE2]"}`;

// Events/Programs with a dropdown of departments (hover/focus on desktop, listed below on phones).
const EventsNav = ({ to, label, onClick, vertical }) => {
  const { pick } = useLang();
  const { settings } = useSite();
  const deps = Array.isArray(settings?.departments) ? settings.departments : [];
  const sub = deps.map((d) => (
    <NavLink key={d.slug} to={`/events/${d.slug}`} onClick={(e) => { e.currentTarget.blur(); onClick?.(); }} data-testid={`nav-dept-${d.slug}${vertical ? "-mobile" : ""}`}
      className={({ isActive }) => `block font-ui transition-colors ${vertical ? "text-lg" : "rounded-lg px-3 py-2 text-sm hover:bg-[#D4AF37]/10"} ${isActive ? "text-[#D4AF37]" : "text-[#F4EEE2]/80 hover:text-[#F4EEE2]"}`}>
      {pick(d.en, d.mh)}
    </NavLink>
  ));
  if (vertical) return (
    <div>
      <NavLink to={to} end onClick={onClick} data-testid="nav-link-gallery-mobile" className={linkCls(true)}>{label}</NavLink>
      {deps.length > 0 && <div className="mt-3 flex flex-col gap-2 border-l border-[#D4AF37]/30 pl-4">{sub}</div>}
    </div>
  );
  return (
    <div className="group relative" data-testid="nav-events-dropdown">
      <NavLink to={to} onClick={onClick} data-testid="nav-link-gallery" className={(s) => `${linkCls(false)(s)} inline-flex items-center gap-1`}>
        {label} {deps.length > 0 && <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180 group-focus-within:rotate-180" />}
      </NavLink>
      {deps.length > 0 && (
        <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 opacity-0 transition-[opacity,visibility] duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
          <div data-testid="nav-events-menu" className="w-64 rounded-2xl border border-[#D4AF37]/20 bg-[#0B111E] p-2 shadow-xl">{sub}</div>
        </div>
      )}
    </div>
  );
};

const NavItems = ({ onClick, vertical }) => {
  const { t, pick } = useLang();
  const { on, pages, settings } = useSite();
  const items = [
    ["/", t("nav.home"), "home"],
    ...orderedMenu(settings, pages).filter((m) => m.visible && (!m.builtIn || on(m.key))).map((m) =>
      m.builtIn ? [m.to, t(m.label), m.key] : [m.to, pick(m.page.title_en, m.page.title_mh), `page-${m.page.slug}`]
    ),
  ];
  return items.map(([to, label, id]) => id === "gallery" ? <EventsNav key={to} to={to} label={label} onClick={onClick} vertical={vertical} /> : (
    <NavLink
      key={to}
      to={to}
      end
      onClick={onClick}
      data-testid={`nav-link-${id}${vertical ? "-mobile" : ""}`}
      className={linkCls(vertical)}
    >
      {label}
    </NavLink>
  ));
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const { playLive } = usePlayer();
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${scrolled ? "border-b border-[#D4AF37]/10 bg-[#0B111E]/75 backdrop-blur-xl" : "border-b border-transparent"}`}
    >
      <div className="mx-auto flex h-24 max-w-[1400px] items-center justify-between px-5 md:h-28 md:px-10">
        <Link to="/" data-testid="header-logo-link">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-6 xl:gap-8 lg:flex">
          <NavItems />
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LangToggle />
          </div>
          <button
            data-testid="header-listen-live-btn"
            onClick={playLive}
            className="hidden items-center gap-2 rounded-full bg-[#F4EEE2] px-4 py-2.5 font-ui text-sm font-semibold text-[#0B111E] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#D4AF37] md:flex"
          >
            <span className="live-dot" /> {t("cta.listen")}
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="rounded-full border border-[#F4EEE2]/20 p-2.5 text-[#F4EEE2] lg:hidden" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full overflow-y-auto border-l-[#D4AF37]/20 bg-[#0B111E] text-[#F4EEE2] sm:max-w-sm">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-14 flex flex-col gap-6">
                <NavItems vertical onClick={() => setOpen(false)} />
              </div>
              <div className="mt-10 flex flex-col gap-4">
                <LangToggle />
                <button
                  data-testid="mobile-listen-live-btn"
                  onClick={() => { playLive(); setOpen(false); }}
                  className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3.5 font-ui font-semibold text-[#0B111E]"
                >
                  <Radio className="h-4 w-4" /> {t("cta.listen")}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
